import React from 'react';
import PropTypes from 'prop-types';
import { QuoteCustomLineItem } from '../../../quotes/otherQuoteEntities';
import ReviewQuoteCustomLineItem from './ReviewQuoteCustomLineItem';
import ReviewQuoteCustomLineItemEditModal from './ReviewQuoteCustomLineItemEditModal';
import QuoteWizardContext from '../../../quotes/QuoteWizardContext';
import classNames from 'classnames';
import ReviewQuoteProductOverrideDiscountModal from '../quote-products/ReviewQuoteProductOverrideDiscountModal';

const AddItemsButton = ({ show, onClick }) => {
  if (!show) return null;
  return (
    <div className="row justify-content-center">
      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={onClick}
      >
        Add custom line item
      </button>
    </div>
  );
};

const Table = ({
  customLineItems,
  onEdit,
  onRemove,
  readOnly,
  show,
  onQuantityUpdated,
  msrpClassNames,
  dNetClassNames,
  selectedPriceMode,
  isoCurrencySymbol,
  onAddOverrideDiscount,
  onEditOverrideDiscount,
  onRemoveOverrideDiscount
}) => {
  if (!show) return null;
  return (
    <table className="table review-product-table table-mobile">
      <thead>
        <tr>
          <th width="44%" className="text-start">
            Details
          </th>
          <th width="12%" className="text-center">
            Lead Time
          </th>
          <th width="44%">
            <div className="row">
              <div className="col-6"></div>
              <div className={classNames(msrpClassNames, 'text-end')}>MSRP</div>
              <div className={classNames(dNetClassNames, 'text-end')}>
                D. NET
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {customLineItems.map((item) => (
          <ReviewQuoteCustomLineItem
            key={item.id}
            item={item}
            onEdit={onEdit}
            onRemove={onRemove}
            readOnly={readOnly}
            onQuantityUpdated={(quantity) =>
              onQuantityUpdated(item.id, quantity)
            }
            msrpClassNames={msrpClassNames}
            dNetClassNames={dNetClassNames}
            selectedPriceMode={selectedPriceMode}
            isoCurrencySymbol={isoCurrencySymbol}
            onAddOverrideDiscount={onAddOverrideDiscount}
            onEditOverrideDiscount={onEditOverrideDiscount}
            onRemoveOverrideDiscount={onRemoveOverrideDiscount}
          />
        ))}
      </tbody>
    </table>
  );
};

export default class ReviewQuoteCustomLineItems extends React.PureComponent {
  constructor(props) {
    super(props);
    this.editModalRef = React.createRef();
    this.overrideDiscountModalRef = React.createRef();
    this.updateLineItems = this.updateLineItems.bind(this);
  }

  static propTypes = {
    customLineItems: PropTypes.arrayOf(
      PropTypes.instanceOf(QuoteCustomLineItem)
    ).isRequired,
    onQuantityUpdated: PropTypes.func.isRequired,
    onAddOverrideDiscount: PropTypes.func.isRequired,
    onEditOverrideDiscount: PropTypes.func.isRequired,
    onRemoveOverrideDiscount: PropTypes.func.isRequired,
    onEdited: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onAdded: PropTypes.func.isRequired,
    msrpClassNames: PropTypes.string.isRequired,
    dNetClassNames: PropTypes.string.isRequired,
    selectedPriceMode: PropTypes.string.isRequired,
    isoCurrencySymbol: PropTypes.string.isRequired,
  };

  updateLineItems(item, edited) {
    if (edited) this.props.onEdited(item);
    else this.props.onAdded(item);
  }

  render() {
    const {
      msrpClassNames,
      dNetClassNames,
      selectedPriceMode,
      isoCurrencySymbol,
      onQuantityUpdated,
      onAddOverrideDiscount,
      onEditOverrideDiscount,
      onRemoveOverrideDiscount
    } = this.props;

    return (
      <QuoteWizardContext.Consumer>
        {({ showCustomLineItems, canPerformAmiOperations }) => {
          if (!showCustomLineItems()) return null;
          const { customLineItems } = this.props;
          return (
            <React.Fragment>
              <div className="card-deck">
                <div className="card mb-4 box-shadow">
                  <div className="card-body">
                    <h6 className="card-title text-center">
                      Custom Line Items
                    </h6>
                    <Table
                      customLineItems={customLineItems}
                      onEdit={(item) =>
                        this.editModalRef.current.edit(item)
                      }
                      onRemove={this.props.onRemove}
                      readOnly={!canPerformAmiOperations}
                      show={customLineItems.length > 0}
                      onQuantityUpdated={onQuantityUpdated}
                      onAddOverrideDiscount={(customLineItemId) => {
                        this.overrideDiscountModalRef.current.addOverrideDiscountFor(
                          customLineItemId
                        )
                      }}
                      onEditOverrideDiscount={(customLineItemId) => {
                        const quoteCustomLineItem = customLineItems.find((x) => x.id === customLineItemId);
                        this.overrideDiscountModalRef.current.editOverrideDiscountFor(
                          customLineItemId,
                          quoteCustomLineItem.userDefinedDiscount
                        )
                      }}
                      onRemoveOverrideDiscount={onRemoveOverrideDiscount}
                      msrpClassNames={msrpClassNames}
                      dNetClassNames={dNetClassNames}
                      selectedPriceMode={selectedPriceMode}
                      isoCurrencySymbol={isoCurrencySymbol}
                    />
                    <AddItemsButton
                      show={canPerformAmiOperations}
                      onClick={() =>
                        this.editModalRef.current.edit(null)
                      }
                    />
                  </div>
                </div>
              </div>
              <ReviewQuoteCustomLineItemEditModal
                ref={this.editModalRef}
                onCustomLineItemEdited={this.updateLineItems}
              />
              <ReviewQuoteProductOverrideDiscountModal
                ref={this.overrideDiscountModalRef}
                onOverrideDiscountCreated={onAddOverrideDiscount}
                onOverrideDiscountUpdated={onEditOverrideDiscount}
              />
            </React.Fragment>
          );
        }}
      </QuoteWizardContext.Consumer>
    );
  }
}
